import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';
import type { Ticket } from '../types';

interface TicketFilters {
  status?:   string;
  priority?: string;
  search?:   string;
  pending?:  string;
  itQueue?:  string;
  limit?:    string | number;
}

// ── Combined data + mutation hook ────────────────────────────────────────────

export function useTickets(filters: TicketFilters = {}) {
  const { toast }                       = useToast();
  const [tickets, setTickets]           = useState<Ticket[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error,   setError]             = useState<string | null>(null);

  const refetch = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
      );
      const res = await api.get<{ tickets: Ticket[]; total: number; page: number; limit: number }>(
        '/tickets', { params }
      );
      // API returns a paginated envelope { tickets, total, page, limit }
      setTickets(Array.isArray(res.data) ? res.data : (res.data.tickets ?? []));
    } catch (e: any) {
      if (!opts?.silent) setError(e?.response?.data?.message || 'Failed to load tickets');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  // Poll so another user's action (approval, assignment, closure) is reflected here
  // without a manual refresh.
  useEffect(() => {
    refetch();
    const interval = setInterval(() => refetch({ silent: true }), 15000);
    return () => clearInterval(interval);
  }, [refetch]);

  const approveTicket = async (id: string, comment?: string): Promise<Ticket> => {
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/approve`, { comment });
      await refetch();
      toast('Ticket approved successfully', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to approve ticket', 'error');
      throw e;
    }
  };

  const rejectTicket = async (id: string, comment: string): Promise<Ticket> => {
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/reject`, { comment });
      await refetch();
      toast('Ticket rejected', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to reject ticket', 'error');
      throw e;
    }
  };

  const completeTicket = async (id: string): Promise<Ticket> => {
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/complete`);
      await refetch();
      toast('Ticket marked as completed', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to complete ticket', 'error');
      throw e;
    }
  };

  const createTicket = async (payload: any): Promise<Ticket> => {
    try {
      const res = await api.post<Ticket>('/tickets', payload);
      toast('Service request submitted', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to submit request', 'error');
      throw e;
    }
  };

  return {
    tickets, loading, error, refetch,
    createTicket, approveTicket, rejectTicket, completeTicket,
  };
}

// ── Single-ticket hook ────────────────────────────────────────────────────────

export function useTicket(id: string | undefined) {
  const [ticket,  setTicket]  = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!id) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<Ticket>(`/tickets/${id}`);
      setTicket(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Ticket not found');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refetch(); }, [refetch]);

  return { ticket, loading, error, refetch, setTicket };
}

// ── Pending approvals (dedicated endpoint: GET /tickets/pending/approvals) ────

export function usePendingApprovals() {
  const { toast }                       = useToast();
  const [tickets, setTickets]           = useState<Ticket[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error,   setError]             = useState<string | null>(null);

  const refetch = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await api.get<Ticket[]>('/tickets/pending/approvals');
      setTickets(res.data);
    } catch (e: any) {
      if (!opts?.silent) setError(e?.response?.data?.message || 'Failed to load pending approvals');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
    const interval = setInterval(() => refetch({ silent: true }), 15000);
    return () => clearInterval(interval);
  }, [refetch]);

  const approveTicket = async (id: string, comment?: string): Promise<Ticket> => {
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/approve`, { comment });
      await refetch();
      toast('Ticket approved successfully', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to approve ticket', 'error');
      throw e;
    }
  };

  const rejectTicket = async (id: string, comment: string): Promise<Ticket> => {
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/reject`, { comment });
      await refetch();
      toast('Ticket rejected', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to reject ticket', 'error');
      throw e;
    }
  };

  return { tickets, loading, error, refetch, approveTicket, rejectTicket };
}

// ── IT queue (dedicated endpoint: GET /tickets/queue/it) ──────────────────────

export function useITQueue() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const refetch = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await api.get<Ticket[]>('/tickets/queue/it');
      setTickets(res.data);
    } catch (e: any) {
      if (!opts?.silent) setError(e?.response?.data?.message || 'Failed to load IT queue');
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
    const interval = setInterval(() => refetch({ silent: true }), 15000);
    return () => clearInterval(interval);
  }, [refetch]);

  return { tickets, loading, error, refetch };
}

// ── Standalone mutation hooks ─────────────────────────────────────────────────

export function useCreateTicket() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const create = async (payload: any): Promise<Ticket> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post<Ticket>('/tickets', payload);
      toast('Service request submitted successfully', 'success');
      return res.data;
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Failed to submit request';
      setError(msg);
      toast(msg, 'error');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { create, loading, error };
}

export function useApproveTicket() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const approve = async (id: string, comment?: string): Promise<Ticket> => {
    setLoading(true);
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/approve`, { comment });
      toast('Ticket approved', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to approve', 'error');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { approve, loading };
}

export function useRejectTicket() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const reject = async (id: string, comment: string): Promise<Ticket> => {
    setLoading(true);
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/reject`, { comment });
      toast('Ticket rejected', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to reject', 'error');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { reject, loading };
}

// ── Pending count for IT Manager badge ────────────────────────────────────────

export function usePendingCount(enabled = false) {
  const [count, setCount] = useState(0);

  const fetch = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await api.get<{ count: number }>('/tickets/pending/count');
      setCount(res.data.count ?? 0);
    } catch {}
  }, [enabled]);

  useEffect(() => {
    fetch();
    const interval = setInterval(fetch, 30_000);
    return () => clearInterval(interval);
  }, [fetch]);

  return { count, refetch: fetch };
}

export function useCompleteTicket() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const complete = async (id: string): Promise<Ticket> => {
    setLoading(true);
    try {
      const res = await api.put<Ticket>(`/tickets/${id}/complete`);
      toast('Ticket completed', 'success');
      return res.data;
    } catch (e: any) {
      toast(e?.response?.data?.message || 'Failed to complete ticket', 'error');
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { complete, loading };
}
